
import { defineComponent, onMounted } from 'vue';
import { AppTable, AppCard, AppFormRow, AppSelect, AppInput, AppButton } from '@/components';
import { TableColumnType, TableConfig } from '@/components/table/types';
import { useAccountList } from '@/modules/account/app/model/list';
import {
    ActivityFilterType,
    AccountAppOptions,
    TransactionType,
    useAccountAppMixins,
    useAccountActions
} from '@/modules/account/app/model/mixins';
import { ROUTE_NAME_MESSAGE } from '@/modules/message/router';
import { ROUTE_NAME_ACCOUNT } from '@/modules/account/router';

export default defineComponent({
    name: ROUTE_NAME_ACCOUNT.APP.INDEX,
    components: { AppTable, AppCard, AppFormRow, AppSelect, AppInput, AppButton },
    setup() {
        const { query, v$, list, total, fetchList } = useAccountList();
        const { routerLink } = useAccountAppMixins();
        const actions = useAccountActions(() => fetchList());

        const table: TableConfig = {
            columns: [
                { type: TableColumnType.Index, label: '#' },
                { type: TableColumnType.Text, label: 'Account ID', key: 'accountId' },
                { type: TableColumnType.Text, label: 'Name', key: 'name' },
                { type: TableColumnType.Text, label: 'Email', key: 'email' },
                { type: TableColumnType.Text, label: 'Facebook ID', key: 'facebookId' },
                { type: TableColumnType.Text, label: 'Ruby', key: 'ruby' },
                { type: TableColumnType.Text, label: 'Credit', key: 'totalCredit' },
                { type: TableColumnType.Text, label: 'Bought credit', key: 'boughtCredit' },
                { type: TableColumnType.Option, label: 'Status', key: 'status', options: AccountAppOptions.Status },
                { type: TableColumnType.Actions, label: 'Actions' }
            ],
            actions: [
                {
                    props: (row: any) => ({
                        icon: 'fa-eye',
                        theme: 'info',
                        tooltip: 'Detail',
                        to: routerLink.detail(row.accountId)
                    }),
                    group: 0
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-plus',
                        theme: 'info',
                        tooltip: 'Add chip',
                        to: { name: ROUTE_NAME_MESSAGE.SEND, query: { accountIds: row.accountId } }
                    }),
                    group: 0
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-hand-holding-heart',
                        theme: 'info',
                        tooltip: 'Credit transaction',
                        to: routerLink.transaction(TransactionType.Credit, row.accountId)
                    }),
                    group: 1
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-hand-holding-usd',
                        theme: 'success',
                        tooltip: 'Payment transaction',
                        to: routerLink.transaction(TransactionType.Payment, row.accountId)
                    }),
                    group: 1
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-hand-holding',
                        theme: 'info',
                        tooltip: 'Ruby transaction',
                        to: routerLink.transaction(TransactionType.Ruby, row.accountId)
                    }),
                    group: 1
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-history',
                        theme: 'info',
                        tooltip: 'Login history',
                        to: routerLink.activity(ActivityFilterType.AccountId.value, row.accountId)
                    })
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-user-lock',
                        theme: 'warning',
                        tooltip: 'Ban account',
                        onclick: () => actions.ban(row.accountId)
                    }),
                    canShow: row => !row.isBanned
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-user-check',
                        theme: 'success',
                        tooltip: 'Unban account',
                        onclick: () => actions.unban(row.accountId)
                    }),
                    canShow: row => Boolean(row.isBanned)
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-comment-slash',
                        theme: 'warning',
                        tooltip: 'Ban chat',
                        onclick: () => actions.banChat(row.accountId)
                    }),
                    canShow: row => !row.isBanChat
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-comment',
                        theme: 'success',
                        tooltip: 'Unban chat',
                        onclick: () => actions.unbanChat(row.accountId)
                    }),
                    canShow: row => Boolean(row.isBanChat)
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-microphone-slash',
                        theme: 'warning',
                        tooltip: 'Mute',
                        onclick: () => actions.mute(row.accountId)
                    }),
                    canShow: row => !row.isMute
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-microphone',
                        theme: 'success',
                        tooltip: 'Unmute',
                        onclick: () => actions.unmute(row.accountId)
                    }),
                    canShow: row => Boolean(row.isMute)
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-user-clock',
                        theme: 'danger',
                        tooltip: 'Delete',
                        onclick: () => actions.delete(row.accountId)
                    }),
                    canShow: row => !row.isDeleting
                },
                {
                    props: (row: any) => ({
                        icon: 'fa-user-md',
                        theme: 'info',
                        tooltip: 'Undelete',
                        onclick: () => actions.undelete(row.accountId)
                    }),
                    canShow: row => Boolean(row.isDeleting)
                }
            ]
        };

        onMounted(fetchList);

        return { query, list, v$, table, total, fetchList, AccountAppOptions };
    }
});
